import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useOrganization } from "../OrganizationContext"
import { useUser } from "../UserContext"
import { useUserRole } from "../UserRoleContext"
import { fetchAccountOverview } from "../api/accountOverview"
import {
  createMonitoringClient,
  enqueueMonitoringRun,
  fetchOrganizationMonitoringRole,
  updateMonitoringClient,
} from "../api/monitoring"
import {
  Badge, Button, Input, Table, TableBody, TableCell, TableContainer,
  TableHead, TableHeaderCell, TableRow,
} from "../components/ui"

const defaultForm = {
  name: "",
  monitoringProfile: "custom_baseline",
  monitoringTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  monitoringFrequency: "daily",
}

const labels = {
  active: "Active", paused: "Paused", needs_setup: "Needs setup",
  not_configured: "Not configured", unavailable: "Unavailable",
  custom_baseline: "Custom baseline", automotive_asc: "Automotive + ASC",
  daily: "Daily", every_3_days: "Every 3 days", weekly: "Weekly",
  monitored: "Monitored", configured: "Configured",
  current: "Current", needs_review: "Needs review", not_mapped: "Not mapped",
  not_applicable: "Automotive workflow",
}

const platformLabel = (state) => labels[state] || "Not configured"
const formatDate = (value) => value ? new Date(value).toLocaleString() : "Never"
const badgeVariant = (value) => {
  if (["active", "healthy", "monitored", "current"].includes(value)) return "accent"
  if (["critical", "needs_setup", "needs_review"].includes(value)) return "outline"
  return "subtle"
}

function PlatformStatus({ state }) {
  return <div className="account-overview__platform"><Badge variant={badgeVariant(state)}>{platformLabel(state)}</Badge></div>
}

export default function Dashboard() {
  const { organizations = [], selectedOrgId } = useOrganization()
  const user = useUser()
  const globalRole = useUserRole()
  const [accounts, setAccounts] = useState([])
  const [memberRole, setMemberRole] = useState(null)
  const [status, setStatus] = useState("loading")
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")
  const [busy, setBusy] = useState("")
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState(defaultForm)
  const [search, setSearch] = useState("")
  const [platformFilter, setPlatformFilter] = useState("all")
  const [readinessFilter, setReadinessFilter] = useState("all")
  const [profileFilter, setProfileFilter] = useState("all")
  const [healthFilter, setHealthFilter] = useState("all")
  const [documentationFilter, setDocumentationFilter] = useState("all")

  const selectedOrg = organizations.find((organization) => organization.id === selectedOrgId)
  const canManage = globalRole === "admin" || ["admin", "member"].includes(memberRole)

  const load = useCallback(async () => {
    if (!selectedOrgId || !user?.id) return
    setStatus("loading")
    try {
      const [rows, role] = await Promise.all([
        fetchAccountOverview(selectedOrgId),
        fetchOrganizationMonitoringRole(selectedOrgId, user.id),
      ])
      setAccounts(rows)
      setMemberRole(role)
      setError("")
      setStatus("ready")
    } catch (loadError) {
      setError(loadError.message || "Unable to load the account overview.")
      setStatus("error")
    }
  }, [selectedOrgId, user?.id])

  useEffect(() => { load() }, [load])

  const filteredAccounts = useMemo(() => accounts.filter((account) => {
    if (!account.name.toLowerCase().includes(search.trim().toLowerCase())) return false
    if (profileFilter !== "all" && account.profile !== profileFilter) return false
    if (readinessFilter !== "all" && account.monitoring.readiness !== readinessFilter) return false
    if (healthFilter !== "all") {
      const health = account.monitoring.readiness === "unavailable" ? "unavailable" : account.monitoring.health
      if (health !== healthFilter) return false
    }
    const documentationStatus = account.measurementDocumentation?.status ||
      (account.profile === "automotive_asc" ? "not_applicable" : "not_mapped")
    if (documentationFilter === "current" && documentationStatus !== "current") return false
    if (documentationFilter === "action" && !["needs_review", "not_mapped"].includes(documentationStatus)) return false
    if (documentationFilter === "not_mapped" && documentationStatus !== "not_mapped") return false
    if (platformFilter === "configured" && !Object.values(account.platforms).some((state) => state !== "not_configured")) return false
    if (platformFilter === "missing" && !Object.values(account.platforms).some((state) => state === "not_configured")) return false
    return true
  }), [accounts, documentationFilter, healthFilter, platformFilter, profileFilter, readinessFilter, search])

  const submitAccount = async (event) => {
    event.preventDefault()
    if (!form.name.trim()) return
    setBusy("create")
    setError("")
    try {
      await createMonitoringClient(selectedOrgId, form)
      setForm(defaultForm)
      setShowCreate(false)
      setMessage("Account created.")
      await load()
    } catch (saveError) {
      setError(saveError.message || "Unable to create the account.")
    } finally {
      setBusy("")
    }
  }

  const deactivate = async (account) => {
    if (!window.confirm(`Deactivate ${account.name}? Its history will be preserved.`)) return
    setBusy(`deactivate:${account.accountId}`)
    try {
      await updateMonitoringClient(selectedOrgId, account.accountId, {
        active: false,
        deactivated_at: new Date().toISOString(),
        monitoring_enabled: false,
      })
      setMessage(`${account.name} was deactivated.`)
      await load()
    } catch (actionError) {
      setError(actionError.message || "Unable to deactivate the account.")
    } finally {
      setBusy("")
    }
  }

  const runMonitoring = async (account) => {
    setBusy(`run:${account.accountId}`)
    try {
      await enqueueMonitoringRun(account.accountId)
      setMessage(`Monitoring queued for ${account.name}.`)
    } catch (actionError) {
      setError(actionError.message || "Unable to queue monitoring.")
    } finally {
      setBusy("")
    }
  }

  return (
    <div className="page-container page-container--wide ui-stack ui-stack--xl">
      <div className="ui-page-header">
        <div className="ui-stack ui-stack--xs">
          <h1 className="ui-page-title">Account overview</h1>
          <p className="ui-subtitle">{selectedOrg?.name || "Organization"} accounts, platform connections, and overnight monitoring coverage.</p>
        </div>
        {canManage && <Button onClick={() => setShowCreate((value) => !value)}>{showCreate ? "Cancel" : "Add account"}</Button>}
      </div>

      {error && <p className="ui-error" role="alert">{error}</p>}
      {message && <p className="ui-hint" role="status">{message}</p>}

      {showCreate && (
        <form className="ui-grid ui-grid--form" onSubmit={submitAccount}>
          <Input label="Account name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
          <label className="ui-field"><span className="ui-label">Profile</span><select className="ui-input" value={form.monitoringProfile} onChange={(event) => setForm({ ...form, monitoringProfile: event.target.value })}><option value="custom_baseline">Custom baseline</option><option value="automotive_asc">Automotive + ASC</option></select></label>
          <Input label="Reporting timezone" value={form.monitoringTimezone} onChange={(event) => setForm({ ...form, monitoringTimezone: event.target.value })} required />
          <label className="ui-field"><span className="ui-label">Frequency</span><select className="ui-input" value={form.monitoringFrequency} onChange={(event) => setForm({ ...form, monitoringFrequency: event.target.value })}><option value="daily">Daily</option><option value="every_3_days">Every 3 days</option><option value="weekly">Weekly</option></select></label>
          <div className="ui-button-row"><Button type="submit" disabled={busy === "create"}>{busy === "create" ? "Creating…" : "Create account"}</Button></div>
        </form>
      )}

      <div className="ui-grid ui-grid--form" aria-label="Account filters">
        <Input label="Search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Account name" />
        <label className="ui-field"><span className="ui-label">Platforms</span><select className="ui-input" value={platformFilter} onChange={(event) => setPlatformFilter(event.target.value)}><option value="all">All platform states</option><option value="configured">Any configured</option><option value="missing">Any missing</option></select></label>
        <label className="ui-field"><span className="ui-label">Monitoring</span><select className="ui-input" value={readinessFilter} onChange={(event) => setReadinessFilter(event.target.value)}><option value="all">All readiness states</option>{["active", "paused", "needs_setup", "not_configured", "unavailable"].map((value) => <option key={value} value={value}>{labels[value]}</option>)}</select></label>
        <label className="ui-field"><span className="ui-label">Profile</span><select className="ui-input" value={profileFilter} onChange={(event) => setProfileFilter(event.target.value)}><option value="all">All profiles</option><option value="custom_baseline">Custom baseline</option><option value="automotive_asc">Automotive + ASC</option></select></label>
        <label className="ui-field"><span className="ui-label">Health</span><select className="ui-input" value={healthFilter} onChange={(event) => setHealthFilter(event.target.value)}><option value="all">All health states</option>{["healthy", "warning", "critical", "unknown", "unavailable"].map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}</select></label>
        <label className="ui-field"><span className="ui-label">Measurement docs</span><select className="ui-input" value={documentationFilter} onChange={(event) => setDocumentationFilter(event.target.value)}><option value="all">All documentation states</option><option value="current">Current</option><option value="action">Needs attention</option><option value="not_mapped">Not mapped</option></select></label>
      </div>

      {status === "loading" && <p className="ui-text--muted">Loading accounts…</p>}
      {status === "error" && <Button variant="outline" onClick={load}>Retry</Button>}
      {status === "ready" && (
        <TableContainer><Table>
          <TableHead><TableRow><TableHeaderCell>Account</TableHeaderCell><TableHeaderCell>GA4</TableHeaderCell><TableHeaderCell>Google Ads</TableHeaderCell><TableHeaderCell>Meta</TableHeaderCell><TableHeaderCell>Measurement docs</TableHeaderCell><TableHeaderCell>Monitoring</TableHeaderCell><TableHeaderCell>Health</TableHeaderCell><TableHeaderCell>Last check</TableHeaderCell><TableHeaderCell align="right">Actions</TableHeaderCell></TableRow></TableHead>
          <TableBody>{filteredAccounts.map((account) => {
            const monitoring = account.monitoring
            const health = monitoring.readiness === "unavailable" ? "unavailable" : monitoring.health
            const documentation = account.measurementDocumentation || {
              status: account.profile === "automotive_asc" ? "not_applicable" : "not_mapped",
              mappedDomainCount: 0,
              currentDomainCount: 0,
            }
            return (
              <TableRow key={account.accountId}>
                <TableCell><div className="account-overview__monitoring"><Link to={`/accounts/${account.accountId}/overview`}><strong>{account.name}</strong></Link><span className="account-overview__secondary">{labels[account.profile]}</span></div></TableCell>
                <TableCell><PlatformStatus state={account.platforms.ga4} /></TableCell>
                <TableCell><PlatformStatus state={account.platforms.googleAds} /></TableCell>
                <TableCell><PlatformStatus state={account.platforms.meta} /></TableCell>
                <TableCell><div className="account-overview__monitoring"><Badge variant={badgeVariant(documentation.status)}>{labels[documentation.status]}</Badge>{documentation.status !== "not_applicable" && <Link className="account-overview__secondary" to={`/accounts/${account.accountId}/connections`}>{documentation.mappedDomainCount ? `${documentation.currentDomainCount}/${documentation.mappedDomainCount} domains current` : "Map GTM and GA4"}</Link>}</div></TableCell>
                <TableCell><div className="account-overview__monitoring"><Badge variant={badgeVariant(monitoring.readiness)}>{labels[monitoring.readiness]}</Badge><span className="account-overview__secondary">{labels[monitoring.frequency]} · {labels[account.profile]} · {monitoring.monitoredPlatformCount}/{monitoring.eligiblePlatformCount} eligible platforms</span></div></TableCell>
                <TableCell><div className="account-overview__monitoring"><Badge variant={badgeVariant(health)}>{health}</Badge>{monitoring.openIssueCount > 0 && <span className="account-overview__secondary">{monitoring.openIssueCount} open issue{monitoring.openIssueCount === 1 ? "" : "s"}</span>}</div></TableCell>
                <TableCell>{formatDate(monitoring.lastSuccessfulScanAt)}</TableCell>
                <TableCell align="right"><div className="ui-button-row"><Button as={Link} to={`/accounts/${account.accountId}/overview`} size="sm" variant="outline">Open</Button>{canManage && <Button as={Link} to={`/accounts/manage?account_id=${account.accountId}`} size="sm" variant="ghost">Edit</Button>}{canManage && monitoring.readiness === "active" && <Button size="sm" variant="ghost" disabled={busy === `run:${account.accountId}`} onClick={() => runMonitoring(account)}>Run</Button>}{canManage && <Button size="sm" variant="ghost" disabled={busy === `deactivate:${account.accountId}`} onClick={() => deactivate(account)}>Deactivate</Button>}</div></TableCell>
              </TableRow>
            )
          })}</TableBody>
        </Table></TableContainer>
      )}
      {status === "ready" && !filteredAccounts.length && <p className="ui-text--muted">No accounts match these filters.</p>}
    </div>
  )
}
